import { useRef, useState, useCallback, useEffect } from 'react';

export interface UseInfiniteScrollOptions {
  /** Pixels before bottom to trigger load. Default: 100 */
  threshold?: number;
  /** Enable/disable infinite scroll. Default: true */
  enabled?: boolean;
}

export interface UseInfiniteScrollReturn {
  /** Callback ref to attach to scrollable container */
  scrollRef: (node: HTMLDivElement | null) => void;
  /** Whether currently loading more data */
  loadingMore: boolean;
  /** Whether there's more data to load */
  hasMore: boolean;
  /** Current page (0-based) */
  page: number;
  /** Set loading state */
  setLoadingMore: (loading: boolean) => void;
  /** Set hasMore state */
  setHasMore: (hasMore: boolean) => void;
  /** Set page state */
  setPage: (page: number) => void;
  /** Reset to initial state (for search, filters) */
  reset: () => void;
}

/**
 * Hook for infinite scroll with auto-loading when near bottom.
 * Uses IntersectionObserver for efficient scroll detection.
 * Uses callback ref to handle late-mounting containers.
 * @param onLoadMore - Callback to load more data (called when threshold reached)
 * @param options - Configuration options
 */
export const useInfiniteScroll = (
  onLoadMore: () => void,
  options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn => {
  const { threshold = 100, enabled = true } = options;

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  // Store latest callback in ref to avoid observer recreation
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const loadingMoreRef = useRef(loadingMore);
  loadingMoreRef.current = loadingMore;

  // Updated via useEffect (not inline) to prevent race condition:
  // IntersectionObserver initial callback can fire between render and commit,
  // seeing hasMore=true before DOM has painted items (sentinel still visible).
  const hasMoreRef = useRef(false);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Callback ref — triggers observer setup when container mounts/unmounts
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  // After each load, check if content fills the container.
  // If not (scrollHeight <= clientHeight), load more to fill the screen.
  // Uses page as dep so it re-checks after each page load.
  useEffect(() => {
    if (!hasMore || !container || loadingMoreRef.current) return;

    const timeoutId = setTimeout(() => {
      if (container.scrollHeight <= container.clientHeight + threshold
          && hasMoreRef.current && !loadingMoreRef.current) {
        onLoadMoreRef.current();
      }
    }, 200);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page, container, threshold]);

  // Setup IntersectionObserver with sentinel element
  // Re-runs when container mounts/unmounts (callback ref)
  useEffect(() => {
    if (!container || !enabled) return;

    // Create sentinel element at the bottom of the scroll container
    let sentinel = sentinelRef.current;
    if (!sentinel) {
      sentinel = document.createElement('div');
      sentinel.style.height = '1px';
      sentinel.style.width = '100%';
      sentinel.style.pointerEvents = 'none';
      sentinel.setAttribute('data-sentinel', 'infinite-scroll');
      sentinelRef.current = sentinel;
    }

    // Append sentinel if not already in container
    if (!container.contains(sentinel)) {
      container.appendChild(sentinel);
    }

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !loadingMoreRef.current && hasMoreRef.current) {
          onLoadMoreRef.current();
        }
      },
      {
        root: container,
        rootMargin: `0px 0px ${threshold}px 0px`,
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    observerRef.current = observer;

    return () => {
      observer.disconnect();
      // Remove sentinel on cleanup
      if (sentinel && container.contains(sentinel)) {
        container.removeChild(sentinel);
      }
    };
  }, [container, enabled, threshold]);

  // Reset function for search/filter changes
  const reset = useCallback(() => {
    setPage(0);
    setHasMore(false);
    hasMoreRef.current = false;
    setLoadingMore(false);
  }, []);

  return {
    scrollRef,
    loadingMore,
    hasMore,
    page,
    setLoadingMore,
    setHasMore,
    setPage,
    reset,
  };
};
